import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { filesystemAPI, conversationDirectoryAPI } from '../../services/api';
import { generateAudio, viewAudio } from '../../services/workflowActions';
import { logger } from '../../utils/logger';
import { useVoiceCatalog, voiceLine } from '../../hooks/useVoiceCatalog';
import type { TTSVoice } from '../../services/audioService';
import { AudioVersionSelector } from './AudioVersionSelector';

interface AudioPreviewProps {
  conversationId?: string;
  className?: string;
}

interface VoiceOption {
  id: string;
  name: string;
  provider: 'elevenlabs' | 'gemini';
  provider_display: string;
  gender: 'male' | 'female' | 'neutral';
  quality: string;
  description: string;
  languages: string[];
  language_count: number;
  language_preview_urls: Record<string, string | undefined>;
  models: string[];
  preview_url: string;
}

interface VoiceoverAudioEntry {
  language_code: string;
  voice_id: string;
  url: string;
  duration: number;
  created_at: string;
  instructions?: string;
}

interface VoiceoverAudioData {
  audioUrl: string | null;
  voiceName: string | null;
  createdAt: string | null;
  duration: number | null;
  scriptPath?: string;
}

interface ProjectState {
  audio?: {
    scriptAudio?: string;
    voiceoverPath?: string;
  };
}

// The studio's voices (GET /api/audio/voices) in this panel's shape. Ids keep the gemini_<name> form the voiceover
// job strips and that earlier voiceovers recorded.
const voiceOption = (v: TTSVoice): VoiceOption => ({
  id: `gemini_${v.id.toLowerCase()}`, name: v.name, provider: 'gemini', provider_display: 'Google Gemini', gender: v.gender,
  quality: voiceLine(v), description: v.summary, languages: ['en-US'], language_count: 1, language_preview_urls: {},
  models: [], preview_url: v.sampleUrl,
});

// Audio Player Component
const AudioPlayer: React.FC<{ 
  audioUrl: string; 
  title?: string;
  isPlaying: boolean;
  onPlayPause: () => void;
  color?: string;
}> = ({ audioUrl, title, isPlaying, onPlayPause, color = '#3b82f6' }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const animationRef = useRef<number | null>(null);
  const waveformData = useRef<number[]>([]);

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
    } else {
      audioRef.current.src = audioUrl;
    }

    const audio = audioRef.current;
    waveformData.current = []; // Reset waveform data for new audio

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
      drawWaveform();
    };

    const handleTimeUpdate = () => {
      // Only update via event when not playing (to avoid conflicts with animation)
      if (!isPlaying) {
        setCurrentTime(audio.currentTime);
        updateWaveformProgress();
      }
    };

    const handleEnded = () => {
      onPlayPause();
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [audioUrl]);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(logger.error);
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  const generateWaveformData = (barCount: number) => {
    if (waveformData.current.length === 0) {
      waveformData.current = Array.from({ length: barCount }, (_, i) => {
        const heightFactor = Math.sin(i * 0.15) * 0.5 + 0.5;
        const randomness = Math.random() * 0.6 + 0.4;
        return heightFactor * randomness;
      });
    }
  };

  const drawWaveform = (animateProgress = false) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;

    const barWidth = 4;
    const barGap = 2;
    const barCount = Math.floor(canvas.width / (barWidth + barGap));
    const centerY = canvas.height / 2;

    generateWaveformData(barCount);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const progress = duration > 0 ? currentTime / duration : 0;
    const progressX = canvas.width * progress;

    for (let i = 0; i < barCount && i < waveformData.current.length; i++) {
      const height = centerY * waveformData.current[i] * 0.9;
      const x = i * (barWidth + barGap);
      const isActive = x <= progressX;

      // Add animation effect during playback
      let animatedHeight = height;
      if (isPlaying && isActive && animateProgress) {
        const waveEffect = Math.sin(Date.now() * 0.01 + i * 0.3) * 0.3 + 1;
        animatedHeight = height * waveEffect;
      }

      // Create gradient for active bars
      if (isActive) {
        const gradient = ctx.createLinearGradient(0, centerY - animatedHeight / 2, 0, centerY + animatedHeight / 2);
        gradient.addColorStop(0, color + '40');
        gradient.addColorStop(0.5, color);
        gradient.addColorStop(1, color + '40');
        ctx.fillStyle = gradient;
      } else {
        ctx.fillStyle = '#e5e7eb';
      }

      // Add rounded corners effect
      const radius = barWidth / 2;
      ctx.beginPath();
      ctx.roundRect(x, centerY - animatedHeight / 2, barWidth, animatedHeight, radius);
      ctx.fill();
    }
  };

  const updateWaveformProgress = () => {
    drawWaveform(false);
  };

  useEffect(() => {
    const animate = () => {
      if (audioRef.current && isPlaying) {
        // Update current time from audio element during animation
        setCurrentTime(audioRef.current.currentTime);
        drawWaveform(true);
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      drawWaveform(false);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isPlaying, currentTime, duration]); // Include dependencies for proper updates

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canvasRef.current || !audioRef.current || duration === 0) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const seekPercent = clickX / canvas.width;
    audioRef.current.currentTime = seekPercent * duration;
  };

  return (
    <div className="space-y-3">
      {title && <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{title}</p>}
      <div className="rounded-2xl border border-slate-200/50 dark:border-slate-700/50 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md p-6 shadow-lg transition-all duration-300 hover:shadow-xl">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="sm"
            className="h-12 w-12 rounded-full p-0 border border-primary/30 hover:scale-110 transition-all duration-300 hover:shadow-lg bg-primary/10"
            onClick={onPlayPause}
            disabled={isLoading}
            style={{ color }}
          >
            {isLoading ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : isPlaying ? (
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 9v6m4-6v6" />
              </svg>
            ) : (
              <svg className="h-6 w-6 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              </svg>
            )}
          </Button>
          <div className="flex-1">
            <div className="flex items-center justify-between text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {isPlaying ? 'Playing...' : isLoading ? 'Loading...' : 'Ready to play'}
            </div>
          </div>
        </div>
        <div className="relative h-16 w-full cursor-pointer rounded-xl bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-700 dark:to-slate-800 border border-slate-200/50 dark:border-slate-600/50 overflow-hidden" onClick={handleCanvasClick}>
          <canvas ref={canvasRef} className="h-full w-full" />
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Loading audio...
              </div>
            </div>
          )}
          {!isLoading && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none opacity-20" />
          )}
        </div>
      </div>
    </div>
  );
};

export const AudioPreview: React.FC<AudioPreviewProps> = (props) => {
  const catalog = useVoiceCatalog();
  const voiceOptions = useMemo(() => [...(catalog?.values() ?? [])].map(voiceOption), [catalog]);
  if (!voiceOptions.length) return <div className={`p-6 text-sm text-muted-foreground ${props.className ?? ''}`}>Loading voices…</div>;
  return <VoiceoverPanel {...props} voiceOptions={voiceOptions} />;
};

const VoiceoverPanel: React.FC<AudioPreviewProps & { voiceOptions: VoiceOption[] }> = ({
  conversationId,
  className = "",
  voiceOptions,
}) => {
  const [selectedVoice, setSelectedVoice] = useState<VoiceOption>(voiceOptions[0]);
  const [samplePlaying, setSamplePlaying] = useState(false);
  const [generatedAudioUrl, setGeneratedAudioUrl] = useState<string | null>(null);
  const [voiceoverData, setVoiceoverData] = useState<VoiceoverAudioData | null>(null);
  const [generatedPlaying, setGeneratedPlaying] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en-US');
  const [genderFilter, setGenderFilter] = useState<'ALL' | 'Male' | 'Female' | 'Neutral'>('ALL');
  const [providerFilter, setProviderFilter] = useState<'ALL' | 'elevenlabs' | 'gemini'>('ALL');
  const [instructions, setInstructions] = useState<string>('');

  // Get available languages from all voices that support multiple languages
  const availableLanguages = Array.from(
    new Set(
      voiceOptions.flatMap(voice => voice.languages)
    )
  ).map(code => {
    // Language display name mapping for unified locale codes
    const languageNames: Record<string, string> = {
      'ar-AR': 'Arabic (UAE)',
      'ar-EG': 'Arabic (Egypt)',
      'ar-SA': 'Arabic (Saudi Arabia)',
      'bn-BD': 'Bengali (Bangladesh)',
      'cs-CZ': 'Czech (Czech Republic)',
      'de-DE': 'German (Germany)',
      'el-GR': 'Greek (Greece)',
      'en-AU': 'English (Australia)',
      'en-GB': 'English (UK)',
      'en-IN': 'English (India)',
      'en-SE': 'English (Sweden)',
      'en-US': 'English (US)',
      'es-ES': 'Spanish (Spain)',
      'es-MX': 'Spanish (Mexico)',
      'es-US': 'Spanish (US)',
      'fr-FR': 'French (France)',
      'hi-IN': 'Hindi (India)',
      'hi-TA': 'Hindi (Tamil)',
      'id-ID': 'Indonesian (Indonesia)',
      'it-IT': 'Italian (Italy)',
      'ja-JP': 'Japanese (Japan)',
      'ko-KR': 'Korean (South Korea)',
      'mr-IN': 'Marathi (India)',
      'nl-NL': 'Dutch (Netherlands)',
      'pl-PL': 'Polish (Poland)',
      'pt-BR': 'Portuguese (Brazil)',
      'ro-RO': 'Romanian (Romania)',
      'ru-RU': 'Russian (Russia)',
      'ta-IN': 'Tamil (India)',
      'te-IN': 'Telugu (India)',
      'th-TH': 'Thai (Thailand)',
      'tr-TR': 'Turkish (Turkey)',
      'uk-UA': 'Ukrainian (Ukraine)',
      'vi-VN': 'Vietnamese (Vietnam)'
    };
    return { code, display: languageNames[code] || code };
  }).sort((a, b) => a.display.localeCompare(b.display));

  // Filter voices based on selected filters
  const filteredVoices = voiceOptions.filter(voice => {
    const languageMatch = voice.languages.includes(selectedLanguage);
    const genderMatch = genderFilter === 'ALL' || voice.gender.toLowerCase() === genderFilter.toLowerCase();
    const providerMatch = providerFilter === 'ALL' || voice.provider === providerFilter;
    return languageMatch && genderMatch && providerMatch;
  });

  // Sort voices to show recommended providers first based on language
  const sortedVoices = [...filteredVoices].sort((a, b) => {
    const isEnglish = selectedLanguage.startsWith('en-');
    const aIsRecommended = isEnglish ? a.provider === 'gemini' : a.provider === 'elevenlabs';
    const bIsRecommended = isEnglish ? b.provider === 'gemini' : b.provider === 'elevenlabs';
    
    if (aIsRecommended && !bIsRecommended) return -1;
    if (!aIsRecommended && bIsRecommended) return 1;
    return a.name.localeCompare(b.name);
  });

  // Handler for voice selection
  const handleVoiceSelect = (voiceId: string) => {
    const voice = voiceOptions.find(v => v.id === voiceId);
    if (voice) {
      setSelectedVoice(voice);
      setSamplePlaying(false); // Stop any currently playing sample
      // Clear instructions if switching to non-Gemini voice
      if (voice.provider !== 'gemini') {
        setInstructions('');
      }
    }
  };

  // Get provider badge color
  const getProviderBadgeColor = (provider: string) => {
    switch (provider) {
      case 'elevenlabs':
        return 'bg-accent text-accent-foreground';
      case 'gemini':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  // Check if voice is recommended for current language
  const isVoiceRecommended = (voice: VoiceOption) => {
    const isEnglish = selectedLanguage.startsWith('en-');
    return isEnglish ? voice.provider === 'gemini' : voice.provider === 'elevenlabs';
  };

  useEffect(() => {
    const fetchVoiceoverAudio = async () => {
      if (!conversationId) {
        return;
      }

      try {
        // Get the conversation's working directory
        const dirResponse = await conversationDirectoryAPI.get(conversationId);
        const workingDirectory = dirResponse.workingDirectory;

        if (!workingDirectory) {
          return;
        }

        // Try to read voiceover-audio.json from the working directory
        const voiceoverPath = workingDirectory.endsWith('/') 
          ? `${workingDirectory}voiceover-audio.json`
          : `${workingDirectory}/voiceover-audio.json`;

        const response = await filesystemAPI.read(voiceoverPath);
        const parsedData = JSON.parse(response.content);
        
        let voiceoverData: VoiceoverAudioData | null = null;
        
        // Check if it's the new array format
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          // Find the latest entry based on created_at
          const latestEntry = parsedData.reduce((latest: VoiceoverAudioEntry, current: VoiceoverAudioEntry) => {
            const latestDate = new Date(latest.created_at).getTime();
            const currentDate = new Date(current.created_at).getTime();
            return currentDate > latestDate ? current : latest;
          });

          // Convert to VoiceoverAudioData format
          voiceoverData = {
            audioUrl: latestEntry.url,
            voiceName: latestEntry.voice_id,
            createdAt: latestEntry.created_at,
            duration: latestEntry.duration,
            scriptPath: undefined
          };
          
          // Update selected voice if voice_id matches
          const matchingVoice = voiceOptions.find(v => 
            v.id === latestEntry.voice_id || v.name === latestEntry.voice_id
          );
          if (matchingVoice) {
            setSelectedVoice(matchingVoice);
          }
        } 
        // Fallback to old single object format
        else if (parsedData && typeof parsedData === 'object' && !Array.isArray(parsedData)) {
          // It's the old format, use it directly
          voiceoverData = parsedData as VoiceoverAudioData;
          
          // Update selected voice if voiceName is available
          if (voiceoverData.voiceName) {
            const matchingVoice = voiceOptions.find(v => v.name === voiceoverData!.voiceName);
            if (matchingVoice) {
              setSelectedVoice(matchingVoice);
            }
          }
        }
        
        // Set the data if we have it
        if (voiceoverData) {
          setVoiceoverData(voiceoverData);
          
          if (voiceoverData.audioUrl) {
            setGeneratedAudioUrl(voiceoverData.audioUrl);
          }
        }
      } catch (err) {
        logger.warn('Could not load voiceover-audio.json:', err);
        // Fallback to reading from state.json
        try {
          const dirResponse = await conversationDirectoryAPI.get(conversationId);
          const workingDirectory = dirResponse.workingDirectory;
          
          if (workingDirectory) {
            const statePath = workingDirectory.endsWith('/') 
              ? `${workingDirectory}state.json`
              : `${workingDirectory}/state.json`;

            const response = await filesystemAPI.read(statePath);
            const state: ProjectState = JSON.parse(response.content);

            if (state.audio?.scriptAudio) {
              setGeneratedAudioUrl(state.audio.scriptAudio);
            }
          }
        } catch (fallbackErr) {
          logger.warn('Could not load project state either:', fallbackErr);
        }
      }
    };

    fetchVoiceoverAudio();
  }, [conversationId]);


  const handleGenerateAudio = async () => {
    if (!conversationId) return;

    // Block Eleven Labs temporarily
    if (selectedVoice.provider === 'elevenlabs') {
      logger.warn('Eleven Labs audio generation temporarily disabled');
      return;
    }

    setIsGenerating(true);

    try {
      // Call the workflow action to generate audio via direct API
      await generateAudio(conversationId, {
        voiceId: selectedVoice.id,
        language: selectedLanguage,
        instructions: instructions || undefined,
      });

      // Navigate to audio tab to show progress (optional)
      await viewAudio(conversationId);

      // Refresh audio display after generation completes
      await fetchGeneratedAudio();

      logger.info('Audio generation completed successfully');
    } catch (error) {
      logger.error('Failed to generate audio:', error);
    } finally {
      setIsGenerating(false);
    }
  };


  const fetchGeneratedAudio = async () => {
    if (!conversationId) return;
    
    try {
      // Get the conversation's working directory
      const dirResponse = await conversationDirectoryAPI.get(conversationId);
      const workingDirectory = dirResponse.workingDirectory;
      
      if (!workingDirectory) {
        logger.warn('No working directory found');
        setIsGenerating(false);
        return;
      }
      
      // Try to read voiceover-audio.json first
      try {
        const voiceoverPath = workingDirectory.endsWith('/') 
          ? `${workingDirectory}voiceover-audio.json`
          : `${workingDirectory}/voiceover-audio.json`;
        
        const response = await filesystemAPI.read(voiceoverPath);
        const parsedData = JSON.parse(response.content);
        
        let voiceoverData: VoiceoverAudioData | null = null;
        
        // Check if it's the new array format
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          // Find the latest entry based on created_at
          const latestEntry = parsedData.reduce((latest: VoiceoverAudioEntry, current: VoiceoverAudioEntry) => {
            const latestDate = new Date(latest.created_at).getTime();
            const currentDate = new Date(current.created_at).getTime();
            return currentDate > latestDate ? current : latest;
          });

          // Convert to VoiceoverAudioData format
          voiceoverData = {
            audioUrl: latestEntry.url,
            voiceName: latestEntry.voice_id,
            createdAt: latestEntry.created_at,
            duration: latestEntry.duration,
            scriptPath: undefined
          };
          
          logger.info('New audio URL found in voiceover-audio.json (array format):', latestEntry.url);
          
          // Update selected voice if voice_id matches
          const matchingVoice = voiceOptions.find(v => 
            v.id === latestEntry.voice_id || v.name === latestEntry.voice_id
          );
          if (matchingVoice) {
            setSelectedVoice(matchingVoice);
          }
        } 
        // Fallback to old single object format
        else if (parsedData && typeof parsedData === 'object' && !Array.isArray(parsedData)) {
          // It's the old format, use it directly
          voiceoverData = parsedData as VoiceoverAudioData;
          
          logger.info('New audio URL found in voiceover-audio.json (object format):', voiceoverData.audioUrl);
          
          // Update selected voice if voiceName is available
          if (voiceoverData.voiceName) {
            const matchingVoice = voiceOptions.find(v => v.name === voiceoverData!.voiceName);
            if (matchingVoice) {
              setSelectedVoice(matchingVoice);
            }
          }
        }
        
        // Set the data if we have it
        if (voiceoverData) {
          setVoiceoverData(voiceoverData);
          
          if (voiceoverData.audioUrl) {
            setGeneratedAudioUrl(voiceoverData.audioUrl);
          }
        }
      } catch (voiceoverErr) {
        logger.warn('Could not read voiceover-audio.json, falling back to state.json:', voiceoverErr);
        
        // Fallback to state.json
        const statePath = workingDirectory.endsWith('/') 
          ? `${workingDirectory}state.json`
          : `${workingDirectory}/state.json`;
        
        const response = await filesystemAPI.read(statePath);
        const state: ProjectState = JSON.parse(response.content);
        
        if (state.audio?.scriptAudio) {
          setGeneratedAudioUrl(state.audio.scriptAudio);
          logger.info('New audio URL found in state.json:', state.audio.scriptAudio);
        }
      }
      
      setIsGenerating(false);
    } catch (err) {
      logger.error('Failed to fetch generated audio:', err);
      setIsGenerating(false);
    }
  };

  const handleRefreshAudio = () => {
    fetchGeneratedAudio();
  };

  if (!conversationId) {
    return (
      <div className={`h-full flex items-center justify-center bg-background ${className}`}>
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium mb-2">No Conversation Selected</h3>
          <p className="text-sm text-muted-foreground">Please select a conversation to use audio generation features and voice selection.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col bg-background ${className}`}>
      {/* Compact Header */}
      <div className="flex-shrink-0 h-14 bg-card/50 backdrop-blur-sm border-b flex items-center px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            </svg>
          </div>
          <div>
            <h1 className="text-sm font-semibold">Voice Generation</h1>
            <p className="text-xs text-muted-foreground">{filteredVoices.length} premium voices available</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-4">
          <div className={`mx-auto space-y-4 ${generatedAudioUrl ? 'max-w-6xl' : 'max-w-3xl'}`}>
            {/* Layout Grid - 2 columns when audio exists, single column when not */}
            <div className={`${generatedAudioUrl ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : ''}`}>
              {/* Voice Selection */}
              <div className="bg-card rounded-lg border shadow-sm overflow-hidden">
                <div className="bg-muted/30 px-4 py-3 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-sm font-semibold">Voice Selection</h2>
                        <p className="text-xs text-muted-foreground">{sortedVoices.length} premium voices</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {sortedVoices.length} voices
                    </Badge>
                  </div>
                </div>
              <div className="p-4 space-y-4">
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Language</label>
                    <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                      <SelectTrigger className="h-8 rounded-md border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-xs">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableLanguages.map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.display}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Provider</label>
                    <Select value={providerFilter} onValueChange={(value: 'ALL' | 'elevenlabs' | 'gemini') => setProviderFilter(value)}>
                      <SelectTrigger className="h-8 rounded-md border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-xs">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All Providers</SelectItem>
                        <SelectItem value="elevenlabs">🔵 ElevenLabs</SelectItem>
                        <SelectItem value="gemini">🟢 Google Gemini</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Gender</label>
                    <Select value={genderFilter} onValueChange={(value: 'ALL' | 'Male' | 'Female' | 'Neutral') => setGenderFilter(value)}>
                      <SelectTrigger className="h-8 rounded-md border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-xs">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All Genders</SelectItem>
                        <SelectItem value="Male">♂ Male</SelectItem>
                        <SelectItem value="Female">♀ Female</SelectItem>
                        <SelectItem value="Neutral">⚡ Neutral</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Voice Selection */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Voice Selection</label>
                  <Select value={selectedVoice.id} onValueChange={handleVoiceSelect}>
                    <SelectTrigger className="h-10 rounded-md border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-xs">
                      <SelectValue placeholder="Select voice" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortedVoices.map((voice) => (
                        <SelectItem key={voice.id} value={voice.id} disabled={voice.provider === 'elevenlabs'}>
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${voice.provider === 'elevenlabs' ? 'line-through opacity-50' : ''}`}>
                                {isVoiceRecommended(voice) ? '* ' : ''}{voice.name}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                voice.provider === 'elevenlabs'
                                  ? 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                                  : getProviderBadgeColor(voice.provider)
                              }`}>
                                {voice.provider === 'elevenlabs' ? 'E (Unavailable)' : 'G'}
                              </span>
                            </div>
                            <span className="text-xs text-slate-500 ml-3">
                              {voice.gender === 'male' ? '♂' : voice.gender === 'female' ? '♀' : '⚡'} {voice.quality}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Instructions Field (Gemini only) */}
                {selectedVoice.provider === 'gemini' && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Voice Instructions (Optional)
                      <span className="ml-1 text-xs text-slate-500">Only for Google Gemini voices</span>
                    </label>
                    <textarea
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder="e.g., Speak in a professional, confident tone with emphasis on key points"
                      className="w-full h-20 px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      maxLength={200}
                    />
                    <div className="text-xs text-slate-500 text-right">
                      {instructions.length}/200 characters
                    </div>
                  </div>
                )}

                {/* Selected Voice Preview */}
                <div className="border rounded-lg p-4 bg-muted/20">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-sm">
                            {isVoiceRecommended(selectedVoice) ? '* ' : ''}{selectedVoice.name}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProviderBadgeColor(selectedVoice.provider)}`}>
                            {selectedVoice.provider_display}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={selectedVoice.gender === 'male' ? 'default' : selectedVoice.gender === 'female' ? 'secondary' : 'outline'} className="text-xs">
                            {selectedVoice.gender === 'male' ? '♂ Male' : selectedVoice.gender === 'female' ? '♀ Female' : '⚡ Neutral'}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {selectedVoice.quality}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {selectedVoice.provider_display}
                          </Badge>
                          {isVoiceRecommended(selectedVoice) && (
                            <Badge variant="default" className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                              Recommended
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-xs text-muted-foreground text-right">
                        <div className="font-medium text-xs">{availableLanguages.find(l => l.code === selectedLanguage)?.display || selectedLanguage}</div>
                        <div className="text-xs">{selectedLanguage}</div>
                        <div className="text-xs mt-1">{selectedVoice.language_count} language{selectedVoice.language_count > 1 ? 's' : ''} supported</div>
                      </div>
                    </div>

                    {/* Voice Sample Player */}
                    {(selectedVoice.language_preview_urls[selectedLanguage] || selectedVoice.preview_url) && (
                      <AudioPlayer
                        audioUrl={selectedVoice.language_preview_urls[selectedLanguage] || selectedVoice.preview_url || ''}
                        title={`Voice Sample Preview (${availableLanguages.find(l => l.code === selectedLanguage)?.display || selectedLanguage})`}
                        isPlaying={samplePlaying}
                        onPlayPause={() => setSamplePlaying(!samplePlaying)}
                        color="#8b5cf6"
                      />
                    )}
                  </div>
                </div>
              </div>

                {/* Generate Button */}
                <div className="flex flex-col items-center gap-2 py-2">
                  {selectedVoice.provider === 'elevenlabs' && (
                    <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-800">
                      ⚠️ ElevenLabs voices are temporarily unavailable
                    </div>
                  )}
                  <Button
                    onClick={handleGenerateAudio}
                    disabled={isGenerating || selectedVoice.provider === 'elevenlabs'}
                    className={`${
                      selectedVoice.provider === 'elevenlabs'
                        ? 'bg-gray-400 cursor-not-allowed opacity-50'
                        : 'bg-primary hover:bg-primary/90'
                    } text-white px-6 py-2 text-sm font-medium shadow-lg shadow-primary/25 border-0 transition-all duration-300 ${
                      selectedVoice.provider !== 'elevenlabs' ? 'hover:shadow-primary/40 hover:scale-[1.02]' : ''
                    } rounded-lg`}
                    title={selectedVoice.provider === 'elevenlabs' ? 'ElevenLabs voices are temporarily unavailable' : ''}
                  >
                    {isGenerating ? (
                      <>
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Generating Audio...
                      </>
                    ) : selectedVoice.provider === 'elevenlabs' ? (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Temporarily Unavailable
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Generate Audio
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Generated Audio - Second Column */}
              {generatedAudioUrl && (
                <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-2xl border border-emerald-200/50 dark:border-emerald-700/50 shadow-2xl overflow-hidden transition-all duration-300 hover:shadow-3xl">
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 px-6 py-4 border-b border-emerald-200/30 dark:border-emerald-700/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/10 to-green-500/10 dark:from-emerald-400/20 dark:to-green-400/20 rounded-xl flex items-center justify-center border border-emerald-200/50 dark:border-emerald-400/30">
                        <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-sm font-medium bg-gradient-to-r from-emerald-600 to-green-600 dark:from-emerald-400 dark:to-green-400 bg-clip-text text-transparent">
                          Generated Audio
                        </h2>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                          Your AI-generated voiceover
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefreshAudio}
                      className="h-7 px-3 bg-emerald-50 dark:bg-emerald-900/50 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-all duration-200 hover:scale-[1.02] text-xs"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </Button>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  <AudioPlayer
                    audioUrl={generatedAudioUrl}
                    title="Your Generated Voiceover"
                    isPlaying={generatedPlaying}
                    onPlayPause={() => setGeneratedPlaying(!generatedPlaying)}
                    color="#10b981"
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-slate-600 dark:text-slate-400">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">Voice:</span>
                        <span>{voiceoverData?.voiceName || selectedVoice.name}</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${getProviderBadgeColor(selectedVoice.provider)}`}>
                          {selectedVoice.provider_display}
                        </span>
                      </div>
                      <span className="text-[10px] bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full">
                        {selectedVoice.quality}
                      </span>
                      {voiceoverData?.createdAt && (
                        <div className="mt-0.5 text-[10px] text-slate-500 dark:text-slate-400">
                          Generated: {new Date(voiceoverData.createdAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Download functionality would go here
                      }}
                      className="h-7 px-3 bg-emerald-50 dark:bg-emerald-900/50 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-all duration-200 hover:scale-[1.02] text-xs"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </Button>
                  </div>

                  {/* Audio Version Selector */}
                  {conversationId && (
                    <div className="pt-4 border-t border-emerald-200/30 dark:border-emerald-700/30">
                      <AudioVersionSelector
                        conversationId={conversationId}
                        onVersionSelected={(version) => {
                          // Update the displayed audio when a version is selected
                          setGeneratedAudioUrl(version.url);
                          fetchGeneratedAudio();
                        }}
                      />
                    </div>
                  )}
                </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
  );
};